import React, { useState, useEffect } from 'react';
import { Trophy, Calendar, MapPin, Award, Loader2, Plane, Rocket, FlaskConical, Bot, Clock } from 'lucide-react';
import { achievementsAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePageContent } from '../hooks/usePageContent';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

// Icon mapping for categories
const iconMap = {
  plane: Plane,
  rocket: Rocket,
  flask: FlaskConical,
  bot: Bot,
  trophy: Trophy,
  award: Award
};

const Achievements = () => {
  const [achievements, setAchievements] = useState([]);
  const [featuredAchievements, setFeaturedAchievements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();
  const { getContent } = usePageContent('achievements');
  
  // Dynamic page title
  usePageTitle('nav.achievements', 'Achievements', 'Başarılarımız');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [achievementsData, categoriesData, featuredData] = await Promise.all([
          achievementsAPI.getAll(),
          achievementsAPI.getCategories(),
          achievementsAPI.getFeatured()
        ]);
        setAchievements(achievementsData);
        setCategories(categoriesData);
        setFeaturedAchievements(featuredData);
      } catch (err) {
        console.error('Failed to fetch achievements:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper to get localized field
  const getLocalizedField = (item, field) => {
    if (!item) return '';
    if (language === 'tr') {
      const trField = item[`${field}_tr`];
      if (trField && trField.trim()) return trField;
    }
    return item[field] || '';
  };

  // Filter achievements by category
  const filteredAchievements = selectedCategory === 'all'
    ? achievements.filter(a => !a.featured)
    : achievements.filter(a => a.category_id === selectedCategory && !a.featured);

  // Group achievements by category for display
  const groupedAchievements = categories.reduce((acc, category) => {
    acc[category.id] = achievements.filter(a => a.category_id === category.id && !a.featured);
    return acc;
  }, {});

  // Get featured color classes
  const getFeaturedStyle = (color) => {
    const styles = {
      yellow: {
        border: '1px solid rgba(255, 215, 0, 0.5)',
        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(15, 23, 42, 0.9) 100%)',
        iconColor: '#FFD700',
        shadowColor: 'rgba(255, 215, 0, 0.3)'
      },
      red: {
        border: '1px solid rgba(239, 68, 68, 0.5)',
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(15, 23, 42, 0.9) 100%)',
        iconColor: '#EF4444',
        shadowColor: 'rgba(239, 68, 68, 0.3)'
      },
      blue: {
        border: '1px solid rgba(0, 240, 255, 0.5)',
        background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.15) 0%, rgba(15, 23, 42, 0.9) 100%)',
        iconColor: '#00F0FF',
        shadowColor: 'rgba(0, 240, 255, 0.3)'
      },
      green: {
        border: '1px solid rgba(34, 197, 94, 0.5)',
        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(15, 23, 42, 0.9) 100%)',
        iconColor: '#22C55E',
        shadowColor: 'rgba(34, 197, 94, 0.3)'
      }
    };
    return styles[color] || styles.yellow;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ background: '#0F172A' }} data-testid="achievements-page">
      {/* Hero Section */}
      <section className="relative py-24 px-[7.6923%] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getContent('hero_image') || 'https://images.unsplash.com/photo-1567521464027-f127ff144326?w=1920'}')`
          }}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.85) 50%, rgba(239, 68, 68, 0.15) 100%)'
          }}></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6" style={{
            background: 'rgba(239, 68, 68, 0.15)',
            borderRadius: '50%'
          }}>
            <Trophy size={40} style={{ color: '#EF4444' }} />
          </div>
          <h1 className="display-huge mb-6" style={{
            color: '#F8FAFC',
            textShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
          }} data-testid="achievements-title">{getContent('hero_title')}</h1>
          <p className="body-large max-w-3xl mx-auto" style={{ color: '#F8FAFC', opacity: 0.9 }}>
            {getContent('hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Featured Achievements */}
      {featuredAchievements.length > 0 && (
        <section className="py-16 px-[7.6923%]" style={{ background: '#0F172A' }}>
          <div className="max-w-[1400px] mx-auto">
            <h2 className="display-medium mb-12 text-center" style={{ color: '#F8FAFC' }}>
              {getContent('featured_title')}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {featuredAchievements.map((achievement) => {
                const style = getFeaturedStyle(achievement.featured_color);
                return (
                  <div
                    key={achievement.id}
                    className="p-8 rounded-2xl transition-all duration-300"
                    style={{
                      border: style.border,
                      background: style.background,
                      boxShadow: `0 0 30px ${style.shadowColor}`
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = `0 0 50px ${style.shadowColor}`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 0 30px ${style.shadowColor}`;
                    }}
                    data-testid={`featured-achievement-${achievement.id}`}
                  >
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 flex items-center justify-center flex-shrink-0 rounded-xl" style={{
                        background: `${style.iconColor}20`
                      }}>
                        <Trophy size={32} style={{ color: style.iconColor }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl font-bold" style={{ color: style.iconColor }}>
                            {getLocalizedField(achievement, 'title')}
                          </span>
                        </div>
                        <p className="body-small mb-3" style={{ color: style.iconColor, opacity: 0.8 }}>
                          {getLocalizedField(achievement, 'ranking')}
                        </p>
                        {achievement.description && (
                          <p className="body-medium leading-relaxed" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                            {getLocalizedField(achievement, 'description')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Category Tabs & Achievements */}
      {categories.map((category) => {
        const categoryAchievements = groupedAchievements[category.id] || [];
        if (categoryAchievements.length === 0) return null;
        
        const CategoryIcon = iconMap[category.icon] || Trophy;
        
        return (
          <section key={category.id} className="py-16 px-[7.6923%]" style={{
            background: category.order % 2 === 0 ? 'rgba(30, 41, 59, 0.4)' : '#0F172A'
          }}>
            <div className="max-w-[1400px] mx-auto">
              <div className="text-center mb-12">
                <h2 className="display-medium mb-2" style={{ color: '#F8FAFC' }}>
                  {getLocalizedField(category, 'name')}
                </h2>
                <p className="body-medium" style={{ color: '#94A3B8' }}>
                  {category.id === 'suas' && (language === 'tr' ? 'RoboNation Öğrenci İnsansız Hava Sistemleri Yarışması' : 'RoboNation Student Unmanned Aerial Systems Competition')}
                  {category.id === 'teknofest' && (language === 'tr' ? 'İnsansız Hava Araçları Yarışması' : 'Unmanned Aerial Vehicles Competition')}
                  {category.id === 'tubitak' && (language === 'tr' ? 'TÜBİTAK Bilim Yarışmaları' : 'TÜBİTAK Science Competitions')}
                  {category.id === 'meb-robot' && (language === 'tr' ? 'MEB Robot Yarışmaları' : 'MEB Robot Competitions')}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryAchievements.map((achievement) => (
                  <div
                    key={achievement.id}
                    className="group relative overflow-hidden rounded-xl transition-all duration-300"
                    style={{
                      border: '1px solid rgba(0, 240, 255, 0.2)',
                      background: 'rgba(15, 23, 42, 0.6)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = '#00F0FF';
                      e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 240, 255, 0.2)';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.2)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    data-testid={`achievement-card-${achievement.id}`}
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      {achievement.image ? (
                        <img
                          src={getImageUrl(achievement.image)}
                          alt={getLocalizedField(achievement, 'title')}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: 'rgba(30, 41, 59, 0.8)' }}>
                          <CategoryIcon size={48} style={{ color: '#64748B' }} />
                        </div>
                      )}
                      <div className="absolute inset-0" style={{
                        background: 'linear-gradient(to top, rgba(15, 23, 42, 0.9) 0%, transparent 60%)'
                      }} />
                      
                      {/* Achievement Badge */}
                      <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full" style={{
                        background: 'rgba(0, 240, 255, 0.2)',
                        border: '1px solid rgba(0, 240, 255, 0.4)'
                      }}>
                        <Award size={20} style={{ color: '#00F0FF' }} />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={14} style={{ color: '#94A3B8' }} />
                        <span className="text-sm" style={{ color: '#94A3B8' }}>{achievement.year}</span>
                      </div>
                      
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#F8FAFC' }}>
                        {getLocalizedField(achievement, 'ranking')}
                      </h3>
                      
                      {achievement.location && (
                        <div className="flex items-center gap-2">
                          <MapPin size={14} style={{ color: '#94A3B8' }} />
                          <span className="text-sm" style={{ color: '#94A3B8' }}>
                            {getLocalizedField(achievement, 'location')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Chronological Timeline Section */}
      {achievements.length > 0 && (
        <section className="py-24 px-[7.6923%]" style={{
          background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.4) 0%, #0F172A 100%)'
        }}>
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{
                background: 'rgba(0, 240, 255, 0.15)',
                borderRadius: '50%',
                boxShadow: '0 0 30px rgba(0, 240, 255, 0.3)'
              }}>
                <Clock size={32} style={{ color: '#00F0FF' }} />
              </div>
              <h2 className="display-medium mb-4" style={{ color: '#F8FAFC' }}>
                {getContent('timeline_title')}
              </h2>
              <p className="body-medium max-w-2xl mx-auto" style={{ color: '#94A3B8' }}>
                {getContent('timeline_subtitle')}
              </p>
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full" style={{
                background: 'linear-gradient(180deg, #00F0FF 0%, rgba(0, 240, 255, 0.1) 100%)',
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.4)'
              }} />

              {/* Timeline Items */}
              {(() => {
                // Group achievements by year and sort descending
                const achievementsByYear = achievements.reduce((acc, achievement) => {
                  const year = achievement.year;
                  if (!acc[year]) acc[year] = [];
                  acc[year].push(achievement);
                  return acc;
                }, {});
                
                const sortedYears = Object.keys(achievementsByYear).sort((a, b) => b - a);
                
                return sortedYears.map((year, yearIndex) => {
                  const yearAchievements = achievementsByYear[year];
                  const isLeft = yearIndex % 2 === 0;
                  
                  return (
                    <div key={year} className="relative mb-16 last:mb-0" data-testid={`timeline-year-${year}`}>
                      {/* Year Badge - Center */}
                      <div className="absolute left-1/2 transform -translate-x-1/2 z-10">
                        <div 
                          className="w-20 h-20 rounded-full flex items-center justify-center font-bold text-xl"
                          style={{
                            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                            border: '3px solid #00F0FF',
                            boxShadow: '0 0 30px rgba(0, 240, 255, 0.5), inset 0 0 20px rgba(0, 240, 255, 0.1)',
                            color: '#00F0FF',
                            textShadow: '0 0 10px rgba(0, 240, 255, 0.8)'
                          }}
                        >
                          {year}
                        </div>
                      </div>

                      {/* Achievement Cards */}
                      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 ${isLeft ? 'lg:pr-[calc(50%+3rem)]' : 'lg:pl-[calc(50%+3rem)]'}`}>
                        {yearAchievements.map((achievement, achIndex) => {
                          const category = categories.find(c => c.id === achievement.category_id);
                          const CategoryIcon = category ? (iconMap[category.icon] || Trophy) : Trophy;
                          const isFeatured = achievement.featured;
                          const featuredStyle = isFeatured ? getFeaturedStyle(achievement.featured_color) : null;
                          
                          return (
                            <div
                              key={achievement.id}
                              className={`relative p-6 rounded-xl transition-all duration-300 ${isLeft ? 'lg:ml-auto' : ''}`}
                              style={{
                                background: isFeatured ? featuredStyle.background : 'rgba(30, 41, 59, 0.6)',
                                border: isFeatured ? featuredStyle.border : '1px solid rgba(0, 240, 255, 0.2)',
                                boxShadow: isFeatured ? `0 0 20px ${featuredStyle.shadowColor}` : 'none',
                                maxWidth: '500px'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                                e.currentTarget.style.boxShadow = isFeatured 
                                  ? `0 0 40px ${featuredStyle.shadowColor}` 
                                  : '0 0 30px rgba(0, 240, 255, 0.3)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                e.currentTarget.style.boxShadow = isFeatured 
                                  ? `0 0 20px ${featuredStyle.shadowColor}` 
                                  : 'none';
                              }}
                              data-testid={`timeline-item-${achievement.id}`}
                            >
                              {/* Connector Line */}
                              <div 
                                className={`hidden lg:block absolute top-1/2 w-12 h-0.5 ${isLeft ? '-right-12' : '-left-12'}`}
                                style={{
                                  background: 'linear-gradient(90deg, rgba(0, 240, 255, 0.5) 0%, rgba(0, 240, 255, 0) 100%)',
                                  transform: isLeft ? 'none' : 'rotate(180deg)'
                                }}
                              />

                              <div className="flex items-start gap-4">
                                {/* Category Icon */}
                                <div 
                                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: isFeatured ? `${featuredStyle.iconColor}20` : 'rgba(0, 240, 255, 0.1)',
                                  }}
                                >
                                  {isFeatured ? (
                                    <Trophy size={24} style={{ color: featuredStyle.iconColor }} />
                                  ) : (
                                    <CategoryIcon size={24} style={{ color: '#00F0FF' }} />
                                  )}
                                </div>

                                <div className="flex-1 min-w-0">
                                  {/* Title */}
                                  <h3 className="text-lg font-bold mb-1" style={{ 
                                    color: isFeatured ? featuredStyle.iconColor : '#F8FAFC' 
                                  }}>
                                    {getLocalizedField(achievement, 'title')}
                                  </h3>
                                  
                                  {/* Ranking */}
                                  <p className="text-sm font-medium mb-2" style={{ 
                                    color: isFeatured ? featuredStyle.iconColor : '#00F0FF',
                                    opacity: 0.9
                                  }}>
                                    {getLocalizedField(achievement, 'ranking')}
                                  </p>

                                  {/* Category & Location */}
                                  <div className="flex flex-wrap items-center gap-3 text-xs" style={{ color: '#94A3B8' }}>
                                    {category && (
                                      <span className="px-2 py-1 rounded-full" style={{
                                        background: 'rgba(0, 240, 255, 0.1)',
                                        border: '1px solid rgba(0, 240, 255, 0.2)'
                                      }}>
                                        {getLocalizedField(category, 'name')}
                                      </span>
                                    )}
                                    {achievement.location && (
                                      <span className="flex items-center gap-1">
                                        <MapPin size={12} />
                                        {getLocalizedField(achievement, 'location')}
                                      </span>
                                    )}
                                  </div>

                                  {/* Description (for featured) */}
                                  {isFeatured && achievement.description && (
                                    <p className="mt-3 text-sm leading-relaxed" style={{ color: '#F8FAFC', opacity: 0.8 }}>
                                      {getLocalizedField(achievement, 'description')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}

              {/* Timeline End Marker */}
              <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-4">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{
                    background: '#00F0FF',
                    boxShadow: '0 0 20px rgba(0, 240, 255, 0.8)'
                  }}
                />
              </div>
            </div>

            {/* Journey Stats */}
            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-xl" style={{
                background: 'rgba(30, 41, 59, 0.4)',
                border: '1px solid rgba(0, 240, 255, 0.2)'
              }}>
                <p className="text-3xl font-bold mb-2" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.5)' }}>
                  {achievements.length}
                </p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  {language === 'tr' ? 'Toplam Başarı' : 'Total Achievements'}
                </p>
              </div>
              <div className="text-center p-6 rounded-xl" style={{
                background: 'rgba(30, 41, 59, 0.4)',
                border: '1px solid rgba(0, 240, 255, 0.2)'
              }}>
                <p className="text-3xl font-bold mb-2" style={{ color: '#FFD700', textShadow: '0 0 10px rgba(255, 215, 0, 0.5)' }}>
                  {featuredAchievements.length}
                </p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  {language === 'tr' ? 'Öne Çıkan' : 'Featured'}
                </p>
              </div>
              <div className="text-center p-6 rounded-xl" style={{
                background: 'rgba(30, 41, 59, 0.4)',
                border: '1px solid rgba(0, 240, 255, 0.2)'
              }}>
                <p className="text-3xl font-bold mb-2" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.5)' }}>
                  {new Set(achievements.map(a => a.year)).size}
                </p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  {language === 'tr' ? 'Aktif Yıl' : 'Active Years'}
                </p>
              </div>
              <div className="text-center p-6 rounded-xl" style={{
                background: 'rgba(30, 41, 59, 0.4)',
                border: '1px solid rgba(0, 240, 255, 0.2)'
              }}>
                <p className="text-3xl font-bold mb-2" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.5)' }}>
                  {categories.length}
                </p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  {language === 'tr' ? 'Yarışma Kategorisi' : 'Competition Categories'}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {achievements.length === 0 && (
        <section className="py-24 px-[7.6923%]">
          <div className="max-w-[1400px] mx-auto text-center">
            <Trophy size={64} className="mx-auto mb-6" style={{ color: '#64748B' }} />
            <p className="body-large" style={{ color: '#94A3B8' }}>
              {t('achievements.noAchievements')}
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default Achievements;
